import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { CATEGORIES, COMING_SOON_LIMITS } from './constants';
import { ComingSoonFormErrors, WhatsNewComingSoonItem } from './types';
import { buildComingSoonId, hasErrors, toNullable, validateComingSoon } from './utils';

interface ComingSoonDialogProps {
  open: boolean;
  /** `null` opens the dialog for a new item. */
  item: WhatsNewComingSoonItem | null;
  /** Ids already in the list, so a new item cannot collide with an existing one. */
  existingIds: string[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars -- parameter name documents the typed callback
  onSave: (item: WhatsNewComingSoonItem) => void;
}

const buildBlankItem = (): WhatsNewComingSoonItem => ({
  id: '',
  title: '',
  category: null,
  planned_for: null
});

const ComingSoonDialog: React.FC<ComingSoonDialogProps> = ({
  open,
  item,
  existingIds,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState<WhatsNewComingSoonItem>(buildBlankItem);
  const [errors, setErrors] = useState<ComingSoonFormErrors>({});
  const [idEdited, setIdEdited] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(item ? { ...item } : buildBlankItem());
    setErrors({});
    setIdEdited(false);
  }, [open, item]);

  const isEditing = Boolean(item);

  useEffect(() => {
    if (isEditing || idEdited) {
      return;
    }
    setForm(previous => ({ ...previous, id: buildComingSoonId(previous.title) }));
  }, [form.title, isEditing, idEdited]);

  const handleSave = () => {
    const normalised: WhatsNewComingSoonItem = {
      ...form,
      title: form.title.trim(),
      category: toNullable(form.category),
      planned_for: toNullable(form.planned_for)
    };

    const validationErrors = validateComingSoon(normalised, isEditing ? [] : existingIds);
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      return;
    }

    onSave(normalised);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='h6' component='span'>
          {isEditing ? 'Edit coming soon item' : 'Add coming soon item'}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label='Title'
              required
              fullWidth
              size='small'
              value={form.title}
              error={Boolean(errors.title)}
              helperText={errors.title ?? `${form.title.length}/${COMING_SOON_LIMITS.title}`}
              inputProps={{ maxLength: COMING_SOON_LIMITS.title }}
              onChange={event => setForm(previous => ({ ...previous, title: event.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label='Category'
              fullWidth
              size='small'
              value={form.category ?? ''}
              error={Boolean(errors.category)}
              helperText={errors.category ?? ' '}
              onChange={event =>
                setForm(previous => ({ ...previous, category: event.target.value }))
              }>
              <MenuItem value=''>None</MenuItem>
              {CATEGORIES.map(category => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Planned for'
              type='date'
              fullWidth
              size='small'
              value={form.planned_for ?? ''}
              helperText='Optional.'
              InputLabelProps={{ shrink: true }}
              onChange={event =>
                setForm(previous => ({ ...previous, planned_for: event.target.value || null }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='ID'
              required
              fullWidth
              size='small'
              value={form.id}
              disabled={isEditing}
              error={Boolean(errors.id)}
              helperText={
                errors.id ??
                (isEditing ? 'Ids cannot be changed once saved.' : 'Generated from the title.')
              }
              inputProps={{ maxLength: COMING_SOON_LIMITS.id }}
              onChange={event => {
                setIdEdited(true);
                setForm(previous => ({ ...previous, id: event.target.value }));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSave}>
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComingSoonDialog;
